import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Grid,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  LinearProgress,
  Paper,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  SwapHoriz,
  CloudUpload,
  IntegrationInstructions,
  CheckCircle,
  Error as ErrorIcon,
  Inventory,
  People,
  LocationOn,
  Assessment,
  PlayArrow,
  Download,
  Refresh,
  Help,
  ArrowForward,
  Storage,
  Delete as DeleteIcon,
  InsertDriveFile
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { migrationApi, MigrationJob, MigrationConfig } from '../../services/dataOnboardingApi';

// Define types that match the API
interface MigrationSourceOption {
  id: string;
  name: string;
  type: string;
  description: string;
  icon: string;
}

interface DataCategory {
  id: string;
  name: string;
  icon: React.ReactElement;
  description: string;
  selected: boolean;
}

const MigrationWizardPage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [migrationSource, setMigrationSource] = useState<string>('');
  const [migrationMethod, setMigrationMethod] = useState<'api' | 'file' | 'manual'>('api');
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationJobId, setMigrationJobId] = useState<string | null>(null);
  const [migrationStatus, setMigrationStatus] = useState<MigrationJob | null>(null);
  const [sources, setSources] = useState<MigrationSourceOption[]>([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploading, setUploading] = useState(false);

  const [dataCategories, setDataCategories] = useState<DataCategory[]>([
    { id: 'products', name: 'Products', icon: <Inventory />, description: 'Product catalog, SKUs, pricing', selected: true },
    { id: 'inventory', name: 'Inventory Levels', icon: <Storage />, description: 'Stock quantities by location', selected: true },
    { id: 'suppliers', name: 'Suppliers', icon: <People />, description: 'Vendor information and contacts', selected: true },
    { id: 'locations', name: 'Warehouses/Locations', icon: <LocationOn />, description: 'Storage locations and facilities', selected: true },
    { id: 'orders', name: 'Purchase Orders', icon: <Assessment />, description: 'Historical order data', selected: false },
  ]);

  // Predefined source options (no getSources API exists)
  useEffect(() => {
    // Set default sources since there's no getSources endpoint
    setSources([
      { id: 'erp', name: 'ERP System', type: 'erp', description: 'Connect to your ERP system', icon: 'storage' },
      { id: 'spreadsheets', name: 'Spreadsheets', type: 'spreadsheets', description: 'Import from Excel/CSV files', icon: 'table' },
      { id: 'wms', name: 'WMS', type: 'wms', description: 'Warehouse Management System', icon: 'warehouse' },
      { id: 'legacy', name: 'Legacy System', type: 'legacy', description: 'Connect to legacy database', icon: 'history' },
      { id: 'custom', name: 'Custom API', type: 'custom', description: 'Custom integration via API', icon: 'code' },
    ]);
    setLoadingSources(false);
  }, []);

  const handleDataCategoryToggle = (categoryId: string) => {
    setDataCategories(dataCategories.map(cat => 
      cat.id === categoryId ? { ...cat, selected: !cat.selected } : cat
    ));
  };

  const startMigration = async () => {
    if (!migrationSource) return;
    
    // For file-based migration, check files are uploaded
    if (migrationMethod === 'file' && uploadedFiles.length === 0) {
      setError('Please upload at least one file to migrate');
      return;
    }
    
    setMigrationRunning(true);
    setError(null);
    
    try {
      const selectedSource = sources.find(s => s.id === migrationSource);
      const config: MigrationConfig = {
        source_type: (selectedSource?.type as MigrationConfig['source_type']) || 'custom',
        source_name: selectedSource?.name || migrationSource,
        data_types: dataCategories.filter(c => c.selected).map(c => c.id),
        field_mappings: {},
      };
      
      const response = await migrationApi.startMigration(config);
      setMigrationJobId(response.migration_id);
      setActiveStep(3); // Move to run step
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to start migration');
      setMigrationRunning(false);
    }
  };

  // File upload handlers
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      // Filter for supported file types
      const validFiles = fileArray.filter(file => 
        file.name.endsWith('.csv') || 
        file.name.endsWith('.xlsx') || 
        file.name.endsWith('.xls') ||
        file.name.endsWith('.json')
      );
      
      if (validFiles.length !== fileArray.length) {
        setError('Some files were skipped. Only CSV, Excel, and JSON files are supported.');
      }
      
      setUploadedFiles(prev => [...prev, ...validFiles]);
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files) {
      const fileArray = Array.from(files);
      const validFiles = fileArray.filter(file => 
        file.name.endsWith('.csv') || 
        file.name.endsWith('.xlsx') || 
        file.name.endsWith('.xls') ||
        file.name.endsWith('.json')
      );
      
      if (validFiles.length !== fileArray.length) {
        setError('Some files were skipped. Only CSV, Excel, and JSON files are supported.');
      }
      
      setUploadedFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const uploadFiles = async () => {
    if (uploadedFiles.length === 0) return;
    
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    
    try {
      const formData = new FormData();
      uploadedFiles.forEach(file => {
        formData.append('files', file);
      });
      
      // Upload to the import endpoint
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/v1/import/upload`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Upload failed');
      }
      
      setUploadProgress(100);
      // After successful upload, start migration
      await startMigration();
    } catch (err: any) {
      setError(err.message || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Poll for status
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (migrationRunning && migrationJobId) {
      const checkStatus = async () => {
        try {
          const status = await migrationApi.getStatus(migrationJobId);
          setMigrationStatus(status);

          if (status.status === 'completed') {
            setMigrationRunning(false);
            setActiveStep(4); // Complete
            clearInterval(intervalId);
          } else if (status.status === 'failed') {
            setMigrationRunning(false);
            setError('Migration failed');
            clearInterval(intervalId);
          }
        } catch (err) {
          console.error("Polling error", err);
        }
      };

      checkStatus();
      intervalId = setInterval(checkStatus, 2000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [migrationRunning, migrationJobId]);

  const steps = [
    'Select Source System',
    'Choose Data to Migrate',
    'Configure Migration',
    'Run Migration',
    'Complete',
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          <SwapHoriz sx={{ mr: 1, verticalAlign: 'middle' }} />
          Data Migration Wizard
        </Typography>
        <Button variant="outlined" startIcon={<Help />} onClick={() => navigate('/help')}>
          Migration Guide
        </Button>
      </Box>

      {/* Progress Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step Content */}
      <Card>
        <CardContent sx={{ p: 4 }}>
          {/* Step 1: Select Source */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Where is your data coming from?</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Select your current inventory/warehouse management system
              </Typography>

              {loadingSources ? (
                <CircularProgress />
              ) : (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {sources.map((source) => (
                    <Grid item xs={6} sm={4} md={2} key={source.id}>
                      <Paper
                        variant={migrationSource === source.id ? 'elevation' : 'outlined'}
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          cursor: 'pointer',
                          border: migrationSource === source.id ? 2 : 1,
                          borderColor: migrationSource === source.id ? 'primary.main' : 'divider',
                          bgcolor: migrationSource === source.id ? 'primary.50' : 'transparent',
                          transition: 'all 0.2s',
                          '&:hover': { borderColor: 'primary.main' },
                        }}
                        onClick={() => setMigrationSource(source.id)}
                      >
                        <Typography variant="h4" sx={{ mb: 1 }}>{source.icon === 'storage' ? '🗄️' : source.icon === 'table' ? '📊' : source.icon === 'warehouse' ? '🏭' : source.icon === 'history' ? '⏳' : '📦'}</Typography>
                        <Typography variant="body2" fontWeight={migrationSource === source.id ? 600 : 400}>
                          {source.name}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}

          {/* Step 2: Choose Data */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>What data do you want to migrate?</Typography>
              <List>
                {dataCategories.map((category) => (
                  <ListItem 
                    key={category.id}
                    sx={{ 
                      border: 1, 
                      borderColor: category.selected ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: category.selected ? 'primary.50' : 'transparent',
                    }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        checked={category.selected}
                        onChange={() => handleDataCategoryToggle(category.id)}
                        color="primary"
                      />
                    </ListItemIcon>
                    <ListItemIcon sx={{ color: category.selected ? 'primary.main' : 'text.secondary' }}>
                      {category.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={category.name}
                      secondary={category.description}
                      primaryTypographyProps={{ fontWeight: category.selected ? 600 : 400 }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Step 3: Configure Migration */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Configure your migration</Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={migrationMethod}
                  onChange={(e) => setMigrationMethod(e.target.value as 'api' | 'file' | 'manual')}
                >
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <FormControlLabel
                      value="api"
                      control={<Radio />}
                      label="Direct API Connection (Recommended)"
                    />
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <FormControlLabel
                      value="file"
                      control={<Radio />}
                      label="File Upload"
                    />
                  </Paper>
                </RadioGroup>
              </FormControl>

              {migrationMethod === 'api' && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>API Credentials</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="API Key" placeholder="Enter your API key" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="API Secret" type="password" placeholder="Enter your API secret" />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {migrationMethod === 'file' && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>Upload Data Files</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload CSV, Excel (.xlsx, .xls), or JSON files containing your supply chain data
                  </Typography>
                  
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".csv,.xlsx,.xls,.json"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />
                  
                  {/* Drag and drop zone */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      border: '2px dashed',
                      borderColor: 'divider',
                      backgroundColor: 'action.hover',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'primary.light',
                        opacity: 0.1,
                      },
                    }}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Drag & Drop Files Here
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      or click to browse
                    </Typography>
                    <Button variant="outlined" startIcon={<CloudUpload />}>
                      Select Files
                    </Button>
                  </Paper>
                  
                  {/* Uploaded files list */}
                  {uploadedFiles.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Selected Files ({uploadedFiles.length})
                      </Typography>
                      <List dense>
                        {uploadedFiles.map((file, index) => (
                          <Paper key={index} variant="outlined" sx={{ mb: 1 }}>
                            <ListItem
                              secondaryAction={
                                <IconButton 
                                  edge="end" 
                                  aria-label="delete"
                                  onClick={() => handleRemoveFile(index)}
                                  disabled={uploading}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              }
                            >
                              <ListItemIcon>
                                <InsertDriveFile color="primary" />
                              </ListItemIcon>
                              <ListItemText
                                primary={file.name}
                                secondary={formatFileSize(file.size)}
                              />
                            </ListItem>
                          </Paper>
                        ))}
                      </List>
                      
                      {uploading && (
                        <Box sx={{ mt: 2 }}>
                          <LinearProgress variant="determinate" value={uploadProgress} />
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                            Uploading... {uploadProgress}%
                          </Typography>
                        </Box>
                      )}
                      
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
                        onClick={uploadFiles}
                        disabled={uploading || uploadedFiles.length === 0}
                        sx={{ mt: 2 }}
                      >
                        {uploading ? 'Uploading...' : 'Upload & Start Migration'}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Step 4: Run Migration */}
          {activeStep === 3 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {migrationRunning ? 'Migration in Progress' : 'Ready to Migrate'}
              </Typography>
              
              {migrationRunning && migrationStatus && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Overall Progress</Typography>
                    <Typography variant="body2" fontWeight={600}>{migrationStatus.progress?.percentage || 0}%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={migrationStatus.progress?.percentage || 0} sx={{ height: 8, borderRadius: 4 }} />
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Current Step: {migrationStatus.progress?.current_step || 0} / {migrationStatus.progress?.total_steps || 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {migrationStatus.progress?.step_name || 'Processing...'}
                  </Typography>
                </Box>
              )}

              {!migrationRunning && !migrationJobId && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<PlayArrow />}
                    onClick={startMigration}
                  >
                    Start Migration
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* Step 5: Complete */}
          {activeStep === 4 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 1 }}>Migration Complete!</Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your data has been successfully migrated to ASCIN
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button variant="outlined" onClick={() => navigate('/supply-chain')}>
                  View Products
                </Button>
                <Button variant="contained" onClick={() => navigate('/dashboard')}>
                  Go to Dashboard
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>

        {/* Navigation Buttons */}
        {activeStep < 3 && (
          <Box sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              disabled={activeStep === 0}
              onClick={() => setActiveStep(activeStep - 1)}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={() => setActiveStep(activeStep + 1)}
              disabled={
                (activeStep === 0 && !migrationSource) ||
                (activeStep === 1 && !dataCategories.some(c => c.selected))
              }
              endIcon={activeStep < 2 ? <ArrowForward /> : undefined}
            >
              {activeStep === 2 ? 'Review & Migrate' : 'Next'}
            </Button>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default MigrationWizardPage;
